import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { IActionType, VARIABLE_TEXT_FEATURE_FLAG_ALL, VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import {
  BuilderComponentGroup,
  BuilderComponentSettingType,
  ComponentSettingKey,
  MdDataListColorStyle,
  MdShape,
} from '../types'
import { LABELS } from '../types/labels'
import { makeClampWidthItems, makeFilter, makeSorting, makeSpacing, makeTypographyGroup } from '../utils/components'

export const TableComponents: BuilderComponentGroup = {
  id: 'table',
  title: LABELS.TABLES_KEY,
  components: [
    {
      compId: 'data-table',
      title: LABELS.DATA_TABLE_KEY,
      searchKeywords: [
        'data table',
        'table',
        'spreadsheet',
        'grid',
        'rows',
        'columns',
        'dataset',
        'records',
        'database',
      ],
      icon: 'Table24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDataSource,
                type: BuilderComponentSettingType.DataTableColumnList,
                label: LABELS.DATA_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignAddButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Add Button Color',
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.COMPONENT_TITLE_KEY,
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                autoDefaultInDetailPage: false,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.TableDataSortBy,
            ComponentSettingKey.TableDataSortType,
            ComponentSettingKey.TableDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.TableDataFilter,
            ComponentSettingKey.TableDataSource
          ),
        },
        designGroup: {
          [LABELS.COLOR_KEY]: {
            items: [
              // makeScheme(ComponentSettingKey.TableDesignScheme),
              // {
              //   id: ComponentSettingKey.TableDesignSearchBgColor,
              //   label: 'Search Box Background',
              //   type: BuilderComponentSettingType.ColorVariant,
              //   defaultValue: 'surface-variant',
              //   colorVariantSettings: {
              //     schemeId: ComponentSettingKey.TableDesignScheme,
              //     fullColor: 'bg',
              //   },
              // },
              // {
              //   id: ComponentSettingKey.TableDesignTitleColor,
              //   label: LABELS.COMPONENT_TITLE_KEY,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     schemeId: ComponentSettingKey.TableDesignScheme,
              //     fullColor: 'fg',
              //   },
              // },
              {
                id: ComponentSettingKey.TableDesignHeaderFooterColor,
                label: LABELS.HEADER_FOOTER_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.TableDesignOddRowColor,
                label: LABELS.ODD_ROW_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-100',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.TableDesignEvenRowColor,
                label: LABELS.EVEN_ROW_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-95',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.TableDesignBorderColor,
                label: LABELS.BORDER_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'fg',
                },
              },
              {
                id: ComponentSettingKey.TableDesignSearchColor,
                label: LABELS.SEARCH_BOX_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.TableDesignSearchColumnFilterColor,
                label: LABELS.COLUMN_FILTER_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.TableDesignScheme,
                  fullColor: 'bg',
                },
              },
            ],
          },
          // Header: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.TableDesignHeader,
          //       label: 'Header',
          //       type: BuilderComponentSettingType.Switch,
          //     },
          //   ],
          // },
          // [LABELS.STYLE_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.TableDesignStrippedRow,
          //       label: LABELS.STRIPPED_ROWS_KEY,
          //       type: BuilderComponentSettingType.Switch,
          //     },
          //   ],
          // },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.TableDesignTypography, [
            {
              id: ComponentSettingKey.TableDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Large',
            },
            {
              id: ComponentSettingKey.TableDesignRowHeaderTypography,
              label: LABELS.ROW_HEADER_KEY,
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.TableDesignRowItemTypography,
              label: 'Row Item',
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.TableDesignFooterTypography,
              label: LABELS.FOOTER_KEY,
              defaultValue: 'Body Medium',
            },
          ]),
          [LABELS.BORDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDesignHorizontalBorder,
                label: LABELS.HORIZONTAL_BORDER_KEY,
                type: BuilderComponentSettingType.Switch,
              },
              {
                id: ComponentSettingKey.TableDesignVerticalBorder,
                label: LABELS.VERTICAL_BORDER_KEY,
                type: BuilderComponentSettingType.Switch,
              },
              {
                id: ComponentSettingKey.TableDesignOutlineBorderWidth,
                label: LABELS.WIDTH_KEY,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [1],
                sliderSettings: {
                  max: 20,
                  min: 0,
                  step: 1,
                  unit: 'px',
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDesignRoundedCorner,
                type: BuilderComponentSettingType.Select,
                label: 'Table',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.TableDesignSearchCorner,
                type: BuilderComponentSettingType.Select,
                label: LABELS.SEARCH_BOX_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.TableDesignAddButtonCornerRadius,
                label: 'Add Button',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.TableDesignWidth, {
                label: 'Table',
              }).items,
              ...makeClampWidthItems(ComponentSettingKey.TableDesignSearchWidth, {
                label: LABELS.SEARCH_BOX_KEY,
              }).items,
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDesignComponentHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignTitleHAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignPaginationHAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'end',
                label: LABELS.PAGINATION_KEY,
              },
              // {
              //   id: ComponentSettingKey.TableDesignFooterHAlign,
              //   type: BuilderComponentSettingType.HorizontalAlignment,
              //   defaultValue: 'end',
              //   label: LABELS.FOOTER_KEY,
              // },
              // {
              //   id: ComponentSettingKey.TableDesignSearchAlignment,
              //   type: BuilderComponentSettingType.HorizontalAlignment,
              //   defaultValue: 'center',
              //   label: LABELS.SEARCH_BOX_KEY,
              // },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDesignTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.TITLE_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignSearchMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.SEARCH_BOX_KEY,
              },
              {
                id: ComponentSettingKey.TableDesignFilterMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.FILTERS_KEY,
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              // {
              //   id: ComponentSettingKey.TableDataItemsPerPage,
              //   label: LABELS.ITEMS_PER_PAGE_KEY,
              //   type: BuilderComponentSettingType.Slider,
              //   defaultValue: [10],
              //   sliderSettings: {
              //     max: 100,
              //     min: 5,
              //     step: 5,
              //   },
              // },
              {
                id: ComponentSettingKey.TableDataSetLimit,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
              {
                id: ComponentSettingKey.TableDesignNumRows,
                label: LABELS.ROWS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 10,
                numberSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.TableDesignShowPagination,
                label: LABELS.SHOW_PAGINATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
            ],
          },
          [LABELS.TEXT_LINES_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TableDesignTextLines,
                label: LABELS.TEXT_LINES_KEY,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [1],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                },
              },
            ],
          },
          // [LABELS.HOVER_EFFECT_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.TableDesignHoverEffect,
          //       type: BuilderComponentSettingType.Select,
          //       label: LABELS.HOVER_EFFECT_KEY,
          //       selectSettings: {
          //         options: [
          //           { value: 'none', label: 'None' },
          //           { value: 'underline', label: 'Underline' },
          //           { value: 'squeeze', label: 'Squeeze' },
          //           { value: 'blur', label: 'Blur Zoom' },
          //           { value: 'neon', label: 'Neon Pulse' },
          //           { value: 'bounce', label: 'Bounce' },
          //         ],
          //       },
          //     },
          //   ],
          // },
        },
        action: [
          {
            id: ComponentSettingKey.TableActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {
                  overlaySettings: {
                    closeBehavior: 'direct',
                    opacity: 85,
                    placement: {
                      desktop: 'left',
                      phone: 'bottom',
                      'phone-landscape': 'left',
                      tablet: 'bottom',
                      'tablet-landscape': 'left',
                      tv: 'left',
                    },
                    shape: {
                      desktop: 'medium',
                      phone: 'medium',
                      'phone-landscape': 'medium',
                      tablet: 'medium',
                      'tablet-landscape': 'medium',
                      tv: 'medium',
                    },
                    size: {
                      desktop: '40%, 100%',
                      phone: '90%, 100%',
                      'phone-landscape': '50%, 100%',
                      tablet: '90%, 100%',
                      'tablet-landscape': '50%, 100%',
                      tv: '40%, 100%',
                    },
                  },
                  screenType: 'overlay',
                },
              },
            ],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
          {
            id: ComponentSettingKey.TableActionItemSelection,
            label: LABELS.ON_ITEM_SELECT_KEY,
            isSimple: true,
            showLabel: true,
            defaultValue: {
              type: IActionType.ShowContextualMenu,
              settings: {},
            },
            dataSource: ComponentSettingKey.TableDataSource,
            actionTypes: [IActionType.ShowContextualMenu],
          },
          {
            id: ComponentSettingKey.TableActionAddButtonClick,
            label: 'On Add Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.FormAddItemScreen,
                settings: {
                  screenType: 'overlay',
                },
              },
            ],
            dataSource: ComponentSettingKey.TableDataSource,
            actionTypes: [IActionType.FormAddItemScreen],
          },
        ],
        behavior: {
          custom: {
            General: {
              items: [
                {
                  id: ComponentSettingKey.TableBehaviorSorting,
                  label: LABELS.SORTABLE_COLUMNS_KEY,
                  type: BuilderComponentSettingType.Switch,
                },
                // {
                //   id: ComponentSettingKey.TableBehaviorPagination,
                //   label: LABELS.PAGINATION_KEY,
                //   type: BuilderComponentSettingType.Switch,
                //   defaultValue: true,
                // },
                // TODO: Temporary hiding this feature, enable this later once data table component are stable
                // See: https://trello.com/c/uFU3tBp4/1304-table-component
                {
                  id: ComponentSettingKey.TableBehaviorRowSelection,
                  label: LABELS.ROW_SELECTION_KEY,
                  type: BuilderComponentSettingType.Switch,
                },
                {
                  id: ComponentSettingKey.TableDesignShowColumnToggle,
                  label: LABELS.SHOW_COLUMN_TOGGLE_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
                {
                  id: ComponentSettingKey.TableDesignShowGlobalFilter,
                  label: LABELS.SEARCH_KEY,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: false,
                },
              ],
            },
            // TODO: Temporary hiding this feature, enable this later once data table component are stable
            // See: https://trello.com/c/uFU3tBp4/1304-table-component
            // 'App Filters': makeUserControlledFilter(
            //   ComponentSettingKey.TableBehaviorUserFilter,
            //   ComponentSettingKey.TableDataSource
            // ),
            // [LABELS.USER_SEARCH_KEY]: {
            //   items: [
            //     {
            //       id: ComponentSettingKey.TableBehaviorUserSearch,
            //       type: BuilderComponentSettingType.Switch,
            //       defaultValue: false,
            //       label: LABELS.ENABLE_USER_SEARCH,
            //     },
            //   ],
            // },
            [LABELS.ADD_BUTTON_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.TableBehaviorAddButtonShow,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.TableDesignAddButtonPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Button Position',
                  defaultValue: 'inline',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.TableBehaviorAddButtonVisibility,
                  type: BuilderComponentSettingType.Visibility,
                  label: 'Show Add Button',
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    {
      compId: 'data-list',
      title: LABELS.SINGLE_ROW_KEY,
      searchKeywords: ['row', 'record', 'entry', 'detail view', 'single item', 'single data row', 'data detail'],
      icon: 'DataList24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        dataGroup: {
          [LABELS.DATA_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListData,
                type: BuilderComponentSettingType.TableColumnList,
                label: LABELS.DATA_KEY,
                tableColumnListSettings: {
                  showCurrentUserLogin: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Phone,
                    ColumnTypes.RichText,
                    ColumnTypes.RowID,
                    ColumnTypes.Emoji,
                    ColumnTypes.Icon,
                    ColumnTypes.QRCode,
                    ColumnTypes.Barcode,
                    ColumnTypes.Color,
                    ColumnTypes.Address,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                    ColumnTypes.Number,
                    ColumnTypes.Rating,
                    ColumnTypes.Boolean,
                    ColumnTypes.DateTime,
                    ColumnTypes.MultiSelect,
                    ColumnTypes.Array,
                    ColumnTypes.ImageArray,
                    ColumnTypes.Image,
                    ColumnTypes.Attachment,
                    ColumnTypes.Coordinate,
                    ColumnTypes.Function,
                    ColumnTypes.Connection,
                    ColumnTypes.Lookup,
                    ColumnTypes.Formula,
                    ColumnTypes.Join,
                    ColumnTypes.Math,
                    ColumnTypes.Template,
                    ColumnTypes.SingleCell,
                  ],
                },
                defaultValue: {
                  table: 'data-list',
                  isDetailPageTable: false,
                  columns: [],
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListHeadline,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLOR_SCHEME_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListDesignColor1,
                label: LABELS.COLOR_1_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-100',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.DataListDesignScheme,
                  fullColor: 'bg',
                },
              },
              {
                id: ComponentSettingKey.DataListDesignColor2,
                label: LABELS.COLOR_2_KEY,
                type: BuilderComponentSettingType.ColorVariant,
                defaultValue: 'primary-95',
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.DataListDesignScheme,
                  fullColor: 'bg',
                },
              },
            ],
          },
          [LABELS.STYLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListDesignVariant,
                type: BuilderComponentSettingType.Select,
                label: LABELS.STYLE_VARIANT_KEY,
                defaultValue: MdDataListColorStyle.Stripped,
                selectSettings: {
                  options: Object.values(MdDataListColorStyle).map((v) => {
                    return {
                      label: v
                        .split('-')
                        .map((s) => {
                          return s.charAt(0).toUpperCase() + s.slice(1)
                        })
                        .join(' '),
                      value: v,
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.DataListDesignWidth),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
                help: 'Controls how the entire component is placed on the screen: aligned to start, center, or end.',
              },
              {
                id: ComponentSettingKey.DataListDesignHeadlineAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
                help: 'Sets how the component’s main title is aligned — start, center, or end.',
              },
              {
                id: ComponentSettingKey.DataListDesignTitleEnd,
                label: LABELS.TITLE_END_ALIGNED_KEY,
                help: 'Moves each label inside the title column to the end edge of its cell (closer to the value).',
                defaultValue: false,
                type: BuilderComponentSettingType.Switch,
              },
              {
                id: ComponentSettingKey.DataListDesignContentEnd,
                label: LABELS.CONTENT_END_ALIGNED_KEY,
                help: 'Moves each value (e.g. email, phone number) to the end edge of its cell for cleaner visual balance.',
                defaultValue: true,
                type: BuilderComponentSettingType.Switch,
              },
            ],
          },
          [LABELS.BORDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListDesignBorderVariant,
                label: LABELS.BORDER_VARIANT_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: 'all',
                selectSettings: {
                  options: [
                    {
                      label: 'None',
                      value: 'none',
                    },
                    {
                      label: 'All',
                      value: 'all',
                    },
                    {
                      label: 'Row',
                      value: 'row',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DataListDesignRoundedCorner,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing({
            margin: {
              id: ComponentSettingKey.DataListDesignMargin,
              defaultValue: { y: 10, x: 10 },
            },
            headlineMargin: {
              id: ComponentSettingKey.DataListDesignHeadlineMargin,
              defaultValue: { y: 10, x: 10 },
            },
          }),
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.DataListDesignTypography, [
            {
              id: ComponentSettingKey.DataListDesignTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.DataListDesignContentTypography,
              label: LABELS.TABLE_CONTENT_KEY,
              defaultValue: 'Body Large',
            },
          ]),
          // [LABELS.HOVER_EFFECT_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.DataListDesignHoverEffect,
          //       type: BuilderComponentSettingType.Select,
          //       label: LABELS.HOVER_EFFECT_KEY,
          //       selectSettings: {
          //         options: hoverEffectOptions,
          //       },
          //     },
          //   ],
          // },
        },
        behavior: {
          visibility: true,
        },
      },
    },
  ],
}
